import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { sendSlackMessage, sendSlackMessageTracked } from "../../models/notifications";
import { PablooLogoWithText } from "../../assets/assets";

// ─── tokens ───────────────────────────────────────────────────────────────────

const T = {
  bg:             "#0c0c0d",
  surface:        "#141416",
  border:         "rgba(255,255,255,0.07)",
  accent:         "#16a34a",
  accentBright:   "#4ade80",
  accentDim:      "rgba(74,222,128,0.12)",
  textPrimary:    "#f1f5f9",
  textSecondary:  "#94a3b8",
  textMuted:      "#4b5563",
  font:           "'DM Sans', system-ui, sans-serif",
};

// ─── data ─────────────────────────────────────────────────────────────────────

const PRODUCT_LINKS = [
  { label: "Store Credit",        href: "/products" },
  { label: "Cashback",            href: "/products/cashback" },
  { label: "Memberships",         href: "/products/memberships" },
  { label: "Store Credit Refunds",href: "/products/store-credit-refunds" },
  { label: "Features",            href: "/features" },
  { label: "Pricing",             href: "/pricing" },
];

const COMPANY_LINKS = [
  { label: "Solutions",        href: "/solutions" },
  { label: "Managed Economy",  href: "/bookCall" },
  { label: "Partnerships",     href: "/partners" },
  { label: "Case studies",     href: "/case-study" },
  { label: "Blog",             href: "/blogs" },
  { label: "Docs",             href: "/docs/quickstart", reload: true },
];

const ACCOUNT_LINKS = [
  { label: "Sign up",    href: "https://app.pabloo.com/sign-up", external: true },
  { label: "Log in",     href: "https://app-extension.pabloo.com", external: true, track: true },
  { label: "Book a demo",href: "/bookCall" },
];

// ─── sub-components ───────────────────────────────────────────────────────────

function FooterLink({ href, external, reload, track, onClick, children }) {
  const style = {
    display:        "block",
    fontSize:       "0.875rem",
    color:          T.textSecondary,
    textDecoration: "none",
    lineHeight:     1,
    padding:        "5px 0",
    transition:     "color 0.15s",
    background:     "none",
    border:         "none",
    cursor:         "pointer",
    fontFamily:     T.font,
    textAlign:      "left",
  };

  const handleMouseEnter = (e) => { e.currentTarget.style.color = T.textPrimary; };
  const handleMouseLeave = (e) => { e.currentTarget.style.color = T.textSecondary; };

  if (onClick) {
    return (
      <button type="button" style={style} onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </button>
    );
  }

  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={style}
        onClick={track ? () => sendSlackMessage("Someone clicked Login in the footer.", "#website-updates") : undefined}
        onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </a>
    );
  }

  if (reload) {
    return (
      <a href={href} style={style} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} style={style} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
    </Link>
  );
}

function ColHeading({ children }) {
  return (
    <p style={{
      fontSize:      "0.6875rem",
      fontWeight:    700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color:         T.textMuted,
      marginBottom:  "20px",
      fontFamily:    T.font,
    }}>
      {children}
    </p>
  );
}

// ─── FooterComponent ──────────────────────────────────────────────────────────

const FooterComponent = () => {
  const navigate = useNavigate();
  const [email, setEmail]   = useState("");
  const [sent,  setSent]    = useState(false);

  const handleSectionClick = (section) => {
    window.dispatchEvent(new CustomEvent("scrollToSection", { detail: { section } }));
    navigate(`/?section=${section}`);
  };

  function handleSubscribe(e) {
    e.preventDefault();
    sendSlackMessageTracked(`🗞 Newsletter subscribe: ${email}`);
    setSent(true);
  }

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <footer style={{ background: T.bg, fontFamily: T.font }}>

      {/* ── Pre-footer CTA ──────────────────────────────────────────────────── */}
      <div
        style={{
          borderTop:    `1px solid ${T.border}`,
          borderBottom: `1px solid ${T.border}`,
          padding:      "72px 24px",
        }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">

            <div style={{ maxWidth: "540px" }}>
              <p style={{
                fontSize:      "0.6875rem",
                fontWeight:    700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color:         T.accentBright,
                marginBottom:  "16px",
              }}>
                Pabloo
              </p>
              <h2 style={{
                fontSize:      "clamp(2rem, 4vw, 2.75rem)",
                fontWeight:    800,
                color:         T.textPrimary,
                lineHeight:    1.15,
                letterSpacing: "-0.03em",
                marginBottom:  "16px",
              }}>
                The economy behind your brand.
              </h2>
              <p style={{
                fontSize:   "1rem",
                color:      T.textSecondary,
                lineHeight: 1.7,
              }}>
                Store credit and cashback infrastructure built for Shopify brands that are serious about retention.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => {
                  sendSlackMessage("Footer CTA: Start for free clicked.", "#website-updates");
                  window.location.href = "https://app.pabloo.com/sign-up";
                }}
                style={{
                  display:       "inline-flex",
                  alignItems:    "center",
                  justifyContent:"center",
                  gap:           "8px",
                  padding:       "14px 28px",
                  borderRadius:  "99px",
                  background:    T.accent,
                  color:         "#ffffff",
                  fontSize:      "0.9375rem",
                  fontWeight:    700,
                  border:        "none",
                  cursor:        "pointer",
                  transition:    "background 0.15s",
                  whiteSpace:    "nowrap",
                  fontFamily:    T.font,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#15803d"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = T.accent; }}
              >
                Start for free
              </button>
              <button
                type="button"
                onClick={() => {
                  sendSlackMessage("Footer CTA: Book a call clicked.", "#website-updates");
                  navigate("/bookCall");
                }}
                style={{
                  display:       "inline-flex",
                  alignItems:    "center",
                  justifyContent:"center",
                  padding:       "14px 28px",
                  borderRadius:  "99px",
                  background:    "transparent",
                  color:         T.textSecondary,
                  fontSize:      "0.9375rem",
                  fontWeight:    600,
                  border:        `1px solid ${T.border}`,
                  cursor:        "pointer",
                  transition:    "border-color 0.15s, color 0.15s",
                  whiteSpace:    "nowrap",
                  fontFamily:    T.font,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                  e.currentTarget.style.color = T.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = T.border;
                  e.currentTarget.style.color = T.textSecondary;
                }}
              >
                Book a call
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* ── Main link grid ──────────────────────────────────────────────────── */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "64px 24px 48px" }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* Brand */}
          <div>
            <a href="/" style={{ display: "inline-block", marginBottom: "16px" }}>
              <img src={PablooLogoWithText} alt="Pabloo" style={{ height: "22px", width: "auto", filter: "brightness(0) invert(1)", opacity: 0.9 }} loading="lazy" />
            </a>
            <p style={{ fontSize: "0.875rem", color: T.textMuted, lineHeight: 1.65, marginBottom: "24px", maxWidth: "220px" }}>
              Store credit &amp; cashback infrastructure for modern Shopify brands.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/pabloo-inc/"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                style={{ color: T.textMuted, transition: "color 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.textPrimary; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.textMuted; }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.7273 0H3.27273C1.46477 0 0 1.46477 0 3.27273V20.7273C0 22.5352 1.46477 24 3.27273 24H20.7273C22.5352 24 24 22.5352 24 20.7273V3.27273C24 1.46477 22.5352 0 20.7273 0ZM8.18182 18.9927H5.01273V9.44727H8.18182V18.9927ZM6.59727 8.59091C5.55545 8.59091 4.71136 7.74682 4.71136 6.70499C4.71136 5.66317 5.55545 4.81909 6.59727 4.81909C7.6391 4.81909 8.48318 5.66317 8.48318 6.70499C8.48318 7.74682 7.6391 8.59091 6.59727 8.59091ZM19.4509 19.0282H16.2V14.7941C16.2 14.1614 16.3855 12.0232 14.5459 12.0232C13.1209 12.0232 12.8305 13.4864 12.7732 14.1436V19.0336H9.60955V9.915H12.78V10.7032C13.3091 9.90818 14.0932 9.29727 15.7664 9.29727C19.4727 9.29727 19.4482 12.7582 19.4482 14.6591L19.4509 19.0282Z"/>
                </svg>
              </a>
              {/* Email */}
              <a
                href="mailto:pabloo@pabloo.com?subject=Pabloo%20Support"
                aria-label="Email"
                style={{ color: T.textMuted, transition: "color 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.textPrimary; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.textMuted; }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14.0057 14.7011C13.4086 15.0992 12.7151 15.3096 12 15.3096C11.285 15.3096 10.5914 15.0992 9.99431 14.7011L0.159797 8.14459C0.105234 8.10822 0.0520781 8.0703 0 8.0313V18.7749C0 20.0067 0.999609 20.9843 2.20936 20.9843H21.7906C23.0224 20.9843 24 19.9847 24 18.7749V8.03125C23.9478 8.07034 23.8945 8.10836 23.8398 8.14478L14.0057 14.7011Z"/>
                  <path d="M0.939844 6.98088L10.7744 13.5375C11.1466 13.7857 11.5733 13.9098 12 13.9098C12.4267 13.9098 12.8534 13.7856 13.2256 13.5375L23.0602 6.98088C23.6487 6.58877 24 5.93252 24 5.22423C24 4.00638 23.0092 3.01562 21.7914 3.01562H2.20861C0.990797 3.01567 0 4.00642 0 5.22541C0 5.93252 0.351375 6.58877 0.939844 6.98088Z"/>
                </svg>
              </a>
              {/* Chat */}
              <button
                type="button"
                aria-label="Chat"
                onClick={() => window.$crisp && window.$crisp.push(["do", "chat:open"])}
                style={{ color: T.textMuted, transition: "color 0.15s", background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.textPrimary; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.textMuted; }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2.05 21.95l4.782-1.387A9.953 9.953 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2Zm0 18a7.95 7.95 0 0 1-4.07-1.118l-.29-.174-3.007.871.888-2.95-.19-.303A7.96 7.96 0 0 1 4 12c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8Z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Product */}
          <div>
            <ColHeading>Product</ColHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {PRODUCT_LINKS.map((l) => (
                <FooterLink key={l.href} href={l.href}>{l.label}</FooterLink>
              ))}
            </div>
          </div>

          {/* Company */}
          <div>
            <ColHeading>Company</ColHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {COMPANY_LINKS.map((l) => (
                <FooterLink key={l.href} href={l.href} reload={l.reload}>{l.label}</FooterLink>
              ))}
              <FooterLink onClick={() => handleSectionClick("testimonials")}>Testimonials</FooterLink>
            </div>
          </div>

          {/* Account */}
          <div>
            <ColHeading>Account</ColHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {ACCOUNT_LINKS.map((l) => (
                <FooterLink key={l.href} href={l.href} external={l.external} track={l.track}>{l.label}</FooterLink>
              ))}
              <FooterLink onClick={() => window.$crisp && window.$crisp.push(["do", "chat:open"])}>Support</FooterLink>
            </div>
          </div>

        </div>
      </div>

      {/* ── Newsletter ──────────────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${T.border}` }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "36px 24px" }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">

            <div>
              <p style={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.accentBright, marginBottom: "4px" }}>
                The Pabloo Brief
              </p>
              <p style={{ fontSize: "0.875rem", color: T.textMuted }}>
                Store credit strategy and e-commerce patterns. Rarely.
              </p>
            </div>

            {sent ? (
              <p style={{ fontSize: "0.875rem", color: T.accentBright, fontWeight: 600, whiteSpace: "nowrap" }}>
                You're in.
              </p>
            ) : (
              <form
                onSubmit={handleSubscribe}
                style={{ display: "flex", gap: "8px", flexShrink: 0 }}
              >
                <label htmlFor="footer-email" className="sr-only">Email address</label>
                <input
                  id="footer-email"
                  type="email"
                  required
                  placeholder="you@brand.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    background:    "#1c1c1e",
                    border:        `1px solid ${T.border}`,
                    borderRadius:  "8px",
                    padding:       "9px 14px",
                    fontSize:      "0.875rem",
                    color:         T.textPrimary,
                    outline:       "none",
                    width:         "220px",
                    fontFamily:    T.font,
                    transition:    "border-color 0.15s",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = T.accent; }}
                  onBlur={(e) => { e.target.style.borderColor = T.border; }}
                />
                <button
                  type="submit"
                  style={{
                    padding:       "9px 20px",
                    borderRadius:  "8px",
                    background:    T.accent,
                    color:         "#ffffff",
                    fontSize:      "0.875rem",
                    fontWeight:    600,
                    border:        "none",
                    cursor:        "pointer",
                    whiteSpace:    "nowrap",
                    fontFamily:    T.font,
                    transition:    "background 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#15803d"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = T.accent; }}
                >
                  Subscribe
                </button>
              </form>
            )}

          </div>
        </div>
      </div>

      {/* ── Bottom bar ──────────────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${T.border}` }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "20px 24px" }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
              <Link
                to="/privacy-policy"
                style={{ fontSize: "0.8125rem", color: T.textMuted, textDecoration: "none", transition: "color 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.textSecondary; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.textMuted; }}
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-and-conditions"
                style={{ fontSize: "0.8125rem", color: T.textMuted, textDecoration: "none", transition: "color 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = T.textSecondary; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = T.textMuted; }}
              >
                Terms of Service
              </Link>
            </div>

            <p style={{ fontSize: "0.8125rem", color: T.textMuted }}>
              © {new Date().getFullYear()} Pabloo, Inc. · San Francisco, CA
            </p>

          </div>
        </div>
      </div>

    </footer>
  );
};

export default FooterComponent;
